import React from 'react';
import { useAuth } from "./context/AuthContext";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useEffect } from 'react';

// Pages
import Landing from './pages/Landing';
import About from './pages/About';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Register from './pages/Register';
import Jobs from './pages/Jobs';
import JobDetails from './pages/JobDetails';
import CandidateDashboard from './pages/CandidateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminUsers from './pages/SuperAdminUsers';
import SuperAdminAdmins from './pages/SuperAdminAdmins';
import SuperAdminJobs from './pages/SuperAdminJobs';
import SuperAdminInterviews from './pages/SuperAdminInterviews';
import SuperAdminTickets from './pages/SuperAdminTickets';
import SuperAdminSubscriptions from './pages/SuperAdminSubscriptions';
import MyJobs from './pages/MyJobs';
import RecruiterRequests from './pages/RecruiterRequests';
import PostJob from './pages/PostJob';
import CodingTest from './pages/CodingTest';
import AIInterview from './pages/AIInterview';
import TechnicalInterview from './pages/TechnicalInterview';
import HRInterview from './pages/HRInterview';
// import AITechnicalInterview from './pages/AITechnicalInterview';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Payment from './pages/Payment';
import Support from './pages/Support';
import InterviewHistory from './pages/InterviewHistory';
import AdminRequest from './pages/AdminRequest';

import JobApplications from './pages/JobApplications';
import MyApplications from './pages/MyApplications';
import ApplicationDetails from './pages/ApplicationDetails';
import JobInterviewHistory from './pages/JobInterviewHistory';
import OfferLetterTemplates from './pages/OfferLetterTemplates';
import ReleaseOfferLetter from './pages/ReleaseOfferLetter';
import InterviewPanel from './pages/InterviewPanel';
import NotFound from './pages/NotFound';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmailOtp from './pages/VerifyEmailOtp';
import CandidateScreening from './pages/CandidateScreening';
import FreeTrialConfirm from './pages/FreeTrialConfirm';
import Analytics from './pages/Analytics';
import CandidateComparison from './pages/CandidateComparison';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import UserPricing from './pages/UserPricing';

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

function AppContent() {
    const sidebarRoutes = [
        '/candidate/dashboard',
        '/admin/dashboard',
        '/admin/jobs',
        '/super-admin/dashboard',
        '/profile',
        '/subscription',
        '/payment',
        '/support',
        '/interview-history',
        '/admin-request',
        '/super-admin/payments-history',
        '/super-admin/admin-history',
        '/super-admin/user-history',
        '/super-admin/interview-history',
        '/super-admin/jobs-history',
        '/super-admin/recruiters-requests',
        '/super-admin/allsubscriptions',
        '/allsupporttickets',
        '/recruiter-applications',
        '/job-interview-history',
        '/admin/offer-letters',
        '/admin/applications/:applicationId/release-offer',
        '/interview-panel',
        '/my-applications',
        '/my-interview-history',
        '/analytics',
        '/analytics/candidate-comparison'
    ];

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppLayout sidebarRoutes={sidebarRoutes} />
        </Router>
    );
}

function AppLayout({ sidebarRoutes }) {
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();

    const getDashboardPath = (u) => {
        if (!u) return '/';
        if (u.role === 'super_admin') return '/super-admin/dashboard';
        if (u.role === 'admin') return '/admin/dashboard';
        return '/candidate/dashboard';
    };

    useEffect(() => {
      window.scrollTo(0, 0);

      // 🔥 Force browser reflow (this is the real fix)
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        document.body.style.overflow = 'auto';
      }, 50);

    }, [location.pathname]);

    const isInterviewPage = location.pathname.includes("/candidate/ai-interview/") ||
                            location.pathname.includes("/candidate/ai-technical-interview/") ||
                            location.pathname.includes("/candidate/ai-hr-interview/");
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const showSidebar = isAuthenticated && (
        sidebarRoutes.some(path => location.pathname === path) ||
        location.pathname.startsWith('/candidate/') ||
        location.pathname.startsWith('/admin/') ||
        location.pathname.startsWith('/super-admin/')
    );

    const noTopPaddingRoutes = ['/', '/about', '/contact'];
    const removeTopPadding = noTopPaddingRoutes.includes(location.pathname);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {!isInterviewPage && <Navbar />}
            <Box sx={{ display: 'flex', flexGrow: 1, zIndex: 1 }}>
                {!isInterviewPage && showSidebar && (
                    <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
                )}

                <Box
                  component="main"
                  key={location.pathname}
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden',
                    pt: isInterviewPage ? 0 : removeTopPadding ? '32px' : '80px',
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                    {showSidebar && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{
                                display: { md: 'none' },
                                position: 'fixed',
                                bottom: 16,
                                right: 16,
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                                zIndex: 10,
                                boxShadow: 3
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1, width: '100%' }}>
                        <Routes>
                            {/* Public Routes */}
                            <Route
                              path="/"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <Landing />
                                )
                              }
                            />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/user-pricing" element={<PrivateRoute adminOnly><UserPricing /></PrivateRoute>} />
                            <Route path="/pricing" element={<PrivateRoute adminOnly><Pricing /></PrivateRoute>} />
                            <Route path="/free-trial-confirm" element={<PrivateRoute adminOnly><FreeTrialConfirm /></PrivateRoute>} />
                            <Route
                              path="/login"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <Login />
                                )
                              }
                            />

                            <Route
                              path="/recruiter-login"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <Login />
                                )
                              }
                            />
                            <Route
                              path="/register"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <Register />
                                )
                              }
                            />
                            <Route path="/verify-email-otp" element={<VerifyEmailOtp />} />

                            <Route
                              path="/forgot-password"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <ForgotPassword />
                                )
                              }
                            />
                            <Route
                              path="/reset-password"
                              element={
                                isAuthenticated ? (
                                  <Navigate to={getDashboardPath(user)} replace />
                                ) : (
                                  <ResetPassword />
                                )
                              }
                            />
                            <Route path="/jobs" element={<Jobs />} />
                            <Route path="/jobs/:id" element={<JobDetails />} />

                            {/* Authenticated Routes */}
                            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                            <Route path="/subscription" element={<PrivateRoute adminOnly><Subscription /></PrivateRoute>} />
                            <Route path="/payment" element={<PrivateRoute adminOnly><Payment /></PrivateRoute>} />
                            <Route path="/support" element={<PrivateRoute><Support /></PrivateRoute>} />
                            <Route path="/interview-history" element={<PrivateRoute><InterviewHistory /></PrivateRoute>} />
                            <Route path="/admin-request" element={<PrivateRoute><AdminRequest /></PrivateRoute>} />

                            {/* Candidate Routes */}
                            <Route path="/candidate/dashboard" element={<PrivateRoute><CandidateDashboard /></PrivateRoute>} />
                            <Route path="/candidate/coding-test/:applicationId" element={<PrivateRoute><CodingTest /></PrivateRoute>} />
                            <Route path="/candidate/ai-interview/:interviewId" element={<PrivateRoute><AIInterview /></PrivateRoute>} />
                            {/* AI Technical Interview — separate session with UUID token, isolated from assessment */}
                            <Route path="/candidate/ai-technical-interview/:sessionToken" element={<PrivateRoute><AIInterview /></PrivateRoute>} />
                            {/* AI HR Interview — independent session with its own UUID token */}
                            <Route path="/candidate/ai-hr-interview/:sessionToken" element={<PrivateRoute><AIInterview /></PrivateRoute>} />
                            <Route path="/candidate/technical-interview/:applicationId" element={<PrivateRoute><TechnicalInterview /></PrivateRoute>} />
                            <Route path="/candidate/hr-interview/:applicationId" element={<PrivateRoute><HRInterview /></PrivateRoute>} />

                            {/* Super Admin Routes */}
                            <Route path="/super-admin/dashboard" element={<PrivateRoute adminOnly><SuperAdminDashboard /></PrivateRoute>} />
                            <Route path="/super-admin/payments-history" element={<PrivateRoute adminOnly><Payment /></PrivateRoute>} />
                            <Route path="/super-admin/admin-history" element={<PrivateRoute adminOnly><SuperAdminAdmins /></PrivateRoute>} />
                            <Route path="/super-admin/user-history" element={<PrivateRoute adminOnly><SuperAdminUsers /></PrivateRoute>} />
                            <Route path="/super-admin/interview-history" element={<PrivateRoute adminOnly><SuperAdminInterviews /></PrivateRoute>} />
                            <Route path="/super-admin/jobs-history" element={<PrivateRoute adminOnly><SuperAdminJobs /></PrivateRoute>} />
                            <Route path="/super-admin/recruiters-requests" element={<PrivateRoute adminOnly><RecruiterRequests /></PrivateRoute>} />
                            <Route path="/super-admin/allsubscriptions" element={<PrivateRoute adminOnly><SuperAdminSubscriptions /></PrivateRoute>} />
                            <Route path="/allsupporttickets" element={<PrivateRoute adminOnly><SuperAdminTickets /></PrivateRoute>} />

                            {/* Shared Protected Routes */}
                            <Route path="/recruiter-applications" element={<PrivateRoute adminOnly><JobApplications /></PrivateRoute>} />
                            <Route path="/job-interview-history" element={<PrivateRoute adminOnly><JobInterviewHistory /></PrivateRoute>} />
                            <Route path="/admin/offer-letters" element={<PrivateRoute adminOnly><OfferLetterTemplates /></PrivateRoute>} />
                            <Route path="/admin/applications/:applicationId/release-offer" element={<PrivateRoute adminOnly><ReleaseOfferLetter /></PrivateRoute>} />
                            <Route path="/interview-panel" element={<PrivateRoute adminOnly><InterviewPanel /></PrivateRoute>} />
                            <Route path="/my-applications" element={<PrivateRoute><MyApplications /></PrivateRoute>} />
                            <Route path="/my-interview-history" element={<PrivateRoute><InterviewHistory /></PrivateRoute>} />
                            <Route path="/admin/dashboard" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
                            <Route path="/admin/jobs" element={<PrivateRoute adminOnly><MyJobs /></PrivateRoute>} />

                            {/* Analytics Routes */}
                            <Route path="/analytics" element={<PrivateRoute adminOnly><Analytics /></PrivateRoute>} />
                            <Route path="/analytics/candidate-comparison" element={<PrivateRoute adminOnly><CandidateComparison /></PrivateRoute>} />

                            <Route path="/admin/jobs/new" element={<PrivateRoute adminOnly><PostJob /></PrivateRoute>} />
                            <Route path="/admin/jobs/:jobId/applications" element={<PrivateRoute adminOnly><JobApplications /></PrivateRoute>} />
                            <Route path="/admin/jobs/:jobId/candidates" element={<PrivateRoute adminOnly><CandidateScreening /></PrivateRoute>} />
                            <Route path="/admin/applications/:id" element={<PrivateRoute adminOnly><ApplicationDetails /></PrivateRoute>} />

                            {/* Fallback Route */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Box>
                </Box>
            </Box>
            {/* Footer moved outside the inner Box to ensure full width */}
            {!isInterviewPage && <Footer />}
        </Box>
    );
}

export default App;
